/**
 * Data Table Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatComponentAlignmentFormat,
  formatFromVariableText,
  formatLimitFormat,
  formatSpacingFormat,
  formatTableDataSource,
  formatTextAlignmentFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseLimitFormat,
  parseSpacingFormat,
  parseTableDataSource,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import { BaseAiComponentInput } from '../../registry'
import type {
  ComponentAlignment,
  HorizontalAlignment,
  LimitInput,
  LimitSettings,
  ResponsiveValue,
  SpacingInputValue,
  TableDataSource,
  TextAlignment,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import dataTableTemplate from './data-table.template.json'

export type { TableDataSource }

const DATA_TABLE_DEFAULT_TEMPLATE = dataTableTemplate as unknown as IDataTableComponentSettings

// todo: convert the datasource structure `columns` into array
export interface IDataTableComponentSettings {
  // Data settings
  'table-data-source': TableDataSource
  'table-data-comp-title': VariableTextValue
  'table-data-set-limit': LimitSettings
  'table-data-sort-type': string

  // Design settings
  'table-design-width': ResponsiveValue<string[] | number[]>
  'table-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'table-design-margin': ResponsiveValue<SpacingInputValue>
  'table-design-title-margin': ResponsiveValue<SpacingInputValue>
  'table-design-title-h-align': ResponsiveValue<TextAlignment>
  'table-design-num-rows': number
  'table-design-text-lines': number[]
  'table-design-rounded-corner': string
  'table-design-outline-border-width': ResponsiveValue<SpacingInputValue>
  'table-design-border-color': string
  'table-design-header-footer-color': string
  'table-design-even-row-color': string
  'table-design-odd-row-color': string
  'table-design-search-width': ResponsiveValue<string[] | number[]>
  'table-design-search-color': any
  'table-design-search-column-filter-color': any
  'table-design-search-corner': string
  'table-design-search-margin': ResponsiveValue<SpacingInputValue>
  'table-design-filter-margin': ResponsiveValue<SpacingInputValue>
  'table-design-add-button-color': string
  'table-design-add-button-corner-radius': string
  'table-design-add-button-position': string
  'table-design-pagination-h-align': HorizontalAlignment
  'table-design-show-pagination': boolean
  'table-design-show-global-filter': boolean
  'table-design-show-column-toggle': boolean
  'table-design-typography': {
    'table-design-title-typography': ResponsiveValue<TypographyValue>
    'table-design-row-header-typography': ResponsiveValue<TypographyValue>
    'table-design-row-item-typography': ResponsiveValue<TypographyValue>
    'table-design-footer-typography': ResponsiveValue<TypographyValue>
  }

  // Actions
  'table-action-item-click': BuilderActions
  'table-action-item-selection': BuilderActions
  'table-action-add-button-click': BuilderActions

  // Behavior settings
  'table-behavior-sorting': boolean
  'table-behavior-row-selection': boolean
  'table-behavior-add-button-show': boolean
}

export interface IDataTableComponent extends IBuilderComponent {
  componentId: 'data-table'
  groupId: 'table'
  settings: IDataTableComponentSettings
}

/**
 * Simplified Column Definition for AI Input
 * Can be:
 * - string: Column ID or compact format
 *   - Simple: "Cf35zy" (just column ID)
 *   - Compact: "col:Cf35zy lbl:ID ic:star" (key-value pairs)
 *     Format: "col:<id> [lbl:<label>] [ic:<icon>] [us:<true|false>] [vs:<visibility>]"
 *     Visibility: "vs:true" (all), "vs:false" (all), or "vs:ph:true ta:false de:true" (responsive)
 * - object: Full column definition with optional properties
 */
export type AiDataTableColumn =
  | string // Column ID or compact format: "Cf35zy" or "col:Cf35zy lbl:ID ic:star"
  | {
      columnId: string // Column ID (required)
      label?: string // Display label (defaults to column ID if not provided)
      icon?: string // Icon name (e.g., "star", "chevron_right")
      visible?: boolean | { phone?: boolean; tablet?: boolean; desktop?: boolean } // Visibility (defaults to true for all breakpoints)
    }

/**
 * Simplified Data Source Structure for AI Input
 */
export interface AiDataTableDataSource {
  tableId: string // Table ID (required)
  columns?: AiDataTableColumn[] // Column definitions - can be strings or objects (if not provided, uses all columns from table)
  isDetailPageTable?: boolean // Whether this is a detail page table
}

/**
 * Data Table AI Input
 */
export interface AiDataTableInput extends BaseAiComponentInput {
  dataSource: string | AiDataTableDataSource | any // Table ID (string), simplified structure, or full complex object
  tableTitle?: string[] // Format: ["text:Data Table"] or ["text:Items in ", "cr:category"]

  // Limit & Sort
  limit?: LimitInput // Number of rows, compact format "limit:<value> [en:<true|false>] [max:<max-limit>]", or limit object
  sortType?: 'asc' | 'desc' // Sort direction

  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Title alignment
  margin?: string // Format: "m:16px"
  titleMargin?: string // Title margin
  numRows?: number // Number of rows to display
  textLines?: number[] // Text line clamp
  cornerRadius?: string // Corner radius
  borderWidth?: string // Border width format: "pt:1px pb:1px"
  borderColor?: string // Border color
  headerFooterColor?: string // Header/footer color
  evenRowColor?: string // Even row color
  oddRowColor?: string // Odd row color

  // Search & Filter
  searchWidth?: string // Search width format: "w:100%"
  searchColor?: string // Search color
  searchColumnFilterColor?: string // Search column filter color
  searchCorner?: string // Search corner radius
  searchMargin?: string // Search margin
  filterMargin?: string // Filter margin
  showGlobalFilter?: boolean // Show global filter
  showColumnToggle?: boolean // Show column toggle

  // Add Button
  addButtonColor?: string // Add button color
  addButtonCornerRadius?: string // Add button corner radius
  addButtonPosition?: string // Add button position
  showAddButton?: boolean // Show add button

  // Pagination
  paginationAlignment?: string // Pagination alignment
  showPagination?: boolean // Show pagination

  // Typography
  typography?: {
    title?: string // Title typography
    rowHeader?: string // Row header typography
    rowItem?: string // Row item typography
    footer?: string // Footer typography
  }

  // Behavior
  allowSorting?: boolean // Allow sorting
  allowRowSelection?: boolean // Allow row selection
}

export class DataTableComp {
  getTemplate(): IDataTableComponentSettings {
    return DATA_TABLE_DEFAULT_TEMPLATE
  }

  /**
   * format data table component settings to AI data table input
   */
  format(settings: IDataTableComponentSettings): AiDataTableInput {
    // @ts-ignore
    const result: AiDataTableInput = {
      type: 'data-table',
      dataSource: '',
    }

    // Data - Source
    if (settings['table-data-source']) {
      const formatted = formatTableDataSource(settings['table-data-source'])
      result.dataSource = formatted || ''
    }

    // Data - Component Title
    if (settings['table-data-comp-title']) {
      result.tableTitle = formatFromVariableText(settings['table-data-comp-title'])
    }

    // Data - Limit
    if (settings['table-data-set-limit']) {
      result.limit = formatLimitFormat(settings['table-data-set-limit'])
    }

    // Data - Sort
    if (settings['table-data-sort-type']) {
      result.sortType = settings['table-data-sort-type'] as 'asc' | 'desc'
    }

    // Design - Width
    if (settings['table-design-width']) {
      result.width = formatWidthFormat(settings['table-design-width'])
    }

    // Design - Alignment
    if (settings['table-design-comp-h-align']) {
      result.alignment =
        formatComponentAlignmentFormat(settings['table-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    if (settings['table-design-title-h-align']) {
      result.titleAlignment =
        formatTextAlignmentFormat(settings['table-design-title-h-align'])?.replace(/^t:/, '') || undefined
    }

    // Design - Margin
    if (settings['table-design-margin']) {
      result.margin = formatSpacingFormat(settings['table-design-margin'])
    }

    if (settings['table-design-title-margin']) {
      result.titleMargin = formatSpacingFormat(settings['table-design-title-margin'])
    }

    // Design - Num Rows
    if (settings['table-design-num-rows'] !== undefined) {
      result.numRows = settings['table-design-num-rows']
    }

    // Design - Text Lines
    if (settings['table-design-text-lines']) {
      result.textLines = settings['table-design-text-lines']
    }

    // Design - Corner Radius
    if (settings['table-design-rounded-corner']) {
      result.cornerRadius = settings['table-design-rounded-corner']
    }

    // Design - Border Width
    if (settings['table-design-outline-border-width']) {
      result.borderWidth = formatSpacingFormat(settings['table-design-outline-border-width'])
    }

    // Design - Colors
    if (settings['table-design-border-color']) {
      result.borderColor = settings['table-design-border-color']
    }

    if (settings['table-design-header-footer-color']) {
      result.headerFooterColor = settings['table-design-header-footer-color']
    }

    if (settings['table-design-even-row-color']) {
      result.evenRowColor = settings['table-design-even-row-color']
    }

    if (settings['table-design-odd-row-color']) {
      result.oddRowColor = settings['table-design-odd-row-color']
    }

    // Design - Search Width
    if (settings['table-design-search-width']) {
      result.searchWidth = formatWidthFormat(settings['table-design-search-width'])
    }

    // Design - Search Colors
    if (settings['table-design-search-color']) {
      result.searchColor = settings['table-design-search-color']
    }

    if (settings['table-design-search-column-filter-color']) {
      result.searchColumnFilterColor = settings['table-design-search-column-filter-color']
    }

    if (settings['table-design-search-corner']) {
      result.searchCorner = settings['table-design-search-corner']
    }

    // Design - Search Margin
    if (settings['table-design-search-margin']) {
      result.searchMargin = formatSpacingFormat(settings['table-design-search-margin'])
    }

    if (settings['table-design-filter-margin']) {
      result.filterMargin = formatSpacingFormat(settings['table-design-filter-margin'])
    }

    // Design - Add Button
    if (settings['table-design-add-button-color']) {
      result.addButtonColor = settings['table-design-add-button-color']
    }

    if (settings['table-design-add-button-corner-radius']) {
      result.addButtonCornerRadius = settings['table-design-add-button-corner-radius']
    }

    if (settings['table-design-add-button-position']) {
      result.addButtonPosition = settings['table-design-add-button-position']
    }

    // Design - Pagination Alignment
    if (settings['table-design-pagination-h-align']) {
      result.paginationAlignment = settings['table-design-pagination-h-align']
    }

    // Design - Typography
    if (settings['table-design-typography']) {
      result.typography = {}
      if (settings['table-design-typography']['table-design-title-typography']) {
        result.typography.title = formatTypographyFormat(
          settings['table-design-typography']['table-design-title-typography']
        )
      }
      if (settings['table-design-typography']['table-design-row-header-typography']) {
        result.typography.rowHeader = formatTypographyFormat(
          settings['table-design-typography']['table-design-row-header-typography']
        )
      }
      if (settings['table-design-typography']['table-design-row-item-typography']) {
        result.typography.rowItem = formatTypographyFormat(
          settings['table-design-typography']['table-design-row-item-typography']
        )
      }
      if (settings['table-design-typography']['table-design-footer-typography']) {
        result.typography.footer = formatTypographyFormat(
          settings['table-design-typography']['table-design-footer-typography']
        )
      }
    }

    // Behavior
    if (settings['table-behavior-sorting'] !== undefined) {
      result.allowSorting = settings['table-behavior-sorting']
    }

    if (settings['table-behavior-row-selection'] !== undefined) {
      result.allowRowSelection = settings['table-behavior-row-selection']
    }

    if (settings['table-behavior-add-button-show'] !== undefined) {
      result.showAddButton = settings['table-behavior-add-button-show']
    }

    if (settings['table-design-show-pagination'] !== undefined) {
      result.showPagination = settings['table-design-show-pagination']
    }

    if (settings['table-design-show-global-filter'] !== undefined) {
      result.showGlobalFilter = settings['table-design-show-global-filter']
    }

    if (settings['table-design-show-column-toggle'] !== undefined) {
      result.showColumnToggle = settings['table-design-show-column-toggle']
    }

    return result
  }

  /**
   * parse AI data table input to data table component settings
   */
  parse(input: AiDataTableInput): IDataTableComponentSettings {
    const result = deepMerge(DATA_TABLE_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['table-data-source'] = parseTableDataSource<TableDataSource>(input.dataSource, {
        tableKey: 'table',
        includeIcon: true,
        isDetailPageTable: true,
      })
    }

    // Data - Component Title
    if (input.tableTitle) {
      result['table-data-comp-title'] = parseToVariableText(input.tableTitle)
    }

    // Data - Limit
    if (input.limit !== undefined) {
      const parsedLimit = parseLimitFormat(input.limit)
      if (parsedLimit) {
        result['table-data-set-limit'] = parsedLimit
      }
    }

    // Data - Sort
    if (input.sortType) {
      result['table-data-sort-type'] = input.sortType
    }

    // Design - Width
    if (input.width) {
      result['table-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['table-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.titleAlignment) {
      const titleAlignParsed = parseAlignmentFormat(input.titleAlignment)
      if (titleAlignParsed.textAlign) {
        result['table-design-title-h-align'] = titleAlignParsed.textAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['table-design-margin'] = parseSpacingFormat(input.margin)
    }

    if (input.titleMargin) {
      result['table-design-title-margin'] = parseSpacingFormat(input.titleMargin)
    }

    // Design - Num Rows
    if (input.numRows !== undefined) {
      result['table-design-num-rows'] = input.numRows
    }

    // Design - Text Lines
    if (input.textLines) {
      result['table-design-text-lines'] = input.textLines
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      result['table-design-rounded-corner'] = input.cornerRadius
    }

    // Design - Border Width
    if (input.borderWidth) {
      result['table-design-outline-border-width'] = parseSpacingFormat(input.borderWidth)
    }

    // Design - Colors
    if (input.borderColor) {
      result['table-design-border-color'] = input.borderColor
    }

    if (input.headerFooterColor) {
      result['table-design-header-footer-color'] = input.headerFooterColor
    }

    if (input.evenRowColor) {
      result['table-design-even-row-color'] = input.evenRowColor
    }

    if (input.oddRowColor) {
      result['table-design-odd-row-color'] = input.oddRowColor
    }

    // Design - Search Width
    if (input.searchWidth) {
      result['table-design-search-width'] = parseWidthFormat(input.searchWidth)
    }

    // Design - Search Colors
    if (input.searchColor) {
      result['table-design-search-color'] = input.searchColor
    }

    if (input.searchColumnFilterColor) {
      result['table-design-search-column-filter-color'] = input.searchColumnFilterColor
    }

    if (input.searchCorner) {
      result['table-design-search-corner'] = input.searchCorner
    }

    // Design - Search Margin
    if (input.searchMargin) {
      result['table-design-search-margin'] = parseSpacingFormat(input.searchMargin)
    }

    if (input.filterMargin) {
      result['table-design-filter-margin'] = parseSpacingFormat(input.filterMargin)
    }

    // Design - Add Button
    if (input.addButtonColor) {
      result['table-design-add-button-color'] = input.addButtonColor
    }

    if (input.addButtonCornerRadius) {
      result['table-design-add-button-corner-radius'] = input.addButtonCornerRadius
    }

    if (input.addButtonPosition) {
      result['table-design-add-button-position'] = input.addButtonPosition
    }

    // Design - Pagination Alignment
    if (input.paginationAlignment) {
      result['table-design-pagination-h-align'] = input.paginationAlignment as HorizontalAlignment
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['table-design-typography']['table-design-title-typography'] = titleTypo
      }

      if (input.typography.rowHeader) {
        const rowHeaderTypo = parseTypographyFormat(input.typography.rowHeader)
        result['table-design-typography']['table-design-row-header-typography'] = rowHeaderTypo
      }

      if (input.typography.rowItem) {
        const rowItemTypo = parseTypographyFormat(input.typography.rowItem)
        result['table-design-typography']['table-design-row-item-typography'] = rowItemTypo
      }

      if (input.typography.footer) {
        const footerTypo = parseTypographyFormat(input.typography.footer)
        result['table-design-typography']['table-design-footer-typography'] = footerTypo
      }
    }

    // Behavior
    if (input.allowSorting !== undefined) {
      result['table-behavior-sorting'] = input.allowSorting
    }

    if (input.allowRowSelection !== undefined) {
      result['table-behavior-row-selection'] = input.allowRowSelection
    }

    if (input.showAddButton !== undefined) {
      result['table-behavior-add-button-show'] = input.showAddButton
    }

    if (input.showPagination !== undefined) {
      result['table-design-show-pagination'] = input.showPagination
    }

    if (input.showGlobalFilter !== undefined) {
      result['table-design-show-global-filter'] = input.showGlobalFilter
    }

    if (input.showColumnToggle !== undefined) {
      result['table-design-show-column-toggle'] = input.showColumnToggle
    }

    return result
  }
}
